// Lo que estaba "en proceso" cuando se cerró o se recargó el editor —
// ADR-2026-11-03-nada-queda-trabado-al-recargar.
//
// Un proceso del Estudio (traducir, borrar un objeto, corregir o cambiar la voz, pasar a
// 1080p, generar una escena) lo sigue la PESTAÑA: si se cierra, nadie mira el resultado.
// Antes, al volver:
//   • la traducción y el cambio de voz quedaban en "processing" para siempre (no se
//     limpiaban al guardar) y bloqueaban el clip: el botón deshabilitado, el agente sin
//     poder tocarlo;
//   • borrar objeto, corregir voz y 1080p se limpiaban en silencio: el clip volvía como
//     si nada, sin decir que el proceso se había cortado;
//   • la escena con IA volvía a "Borrador · Generar" sin avisar que ya se había pedido;
//   • un clip cuya subida no terminó volvía negro, sin archivo y sin aviso.
// Ahora cada uno vuelve con un motivo visible (los badges de error ya existen) o, si se
// puede, se retoma: la traducción guarda su `aiTranslateJobId` y se vuelve a seguir.

/** Lo mínimo de un clip que esto necesita mirar (el editor pasa su `TimelineClip`). */
export interface ClipConProcesos {
  id: string;
  type: string;
  src?: string;
  storagePath?: string;
  assetId?: string;
  imageUrl?: string;
  aiStatus?: string;
  aiError?: string;
  aiExecutionId?: string;
  aiTranslating?: boolean;
  aiTranslateJobId?: string;
  aiTranslateError?: string;
  eraseStatus?: string;
  eraseJobId?: string;
  eraseError?: string;
  corregirVozStatus?: string;
  corregirVozJobId?: string;
  corregirVozError?: string;
  pasarA1080pStatus?: string;
  pasarA1080pError?: string;
  voiceChangeStatus?: string;
  voiceChangeError?: string;
  loadError?: string;
  /** Con qué se borró el objeto (`bria` o `sam2`): para retomar el seguimiento. */
  eraseSabor?: string;
  /** Desde qué segundo del archivo se pidió el borrado: el resultado empieza ahí
   *  (ADR-2026-11-04-el-borrado-se-cobra-y-va-por-tramo). */
  eraseDesde?: number;
  /** El archivo sobre el que se pidió corregir la voz: si al terminar el clip ya es
   *  otro, el resultado queda en Archivos y no lo pisa. */
  corregirVozDesde?: string;
  /** El execution_id (del cliente) del paso a 1080p: para retomarlo al abrir. */
  pasarA1080pExecId?: string;
  /** "Mejorar calidad" (Topaz): cobra al arrancar, así que se retoma sí o sí. */
  mejorarCalidadStatus?: string;
  mejorarCalidadJobId?: string;
  /** Desde qué segundo del archivo se pidió la mejora (como `eraseDesde`). */
  mejorarCalidadDesde?: number;
}

/** Un proceso que la pestaña estaba siguiendo y se vuelve a seguir al abrir
 *  (ADR-2026-11-03-los-procesos-se-retoman-al-volver). */
export type ProcesoARetomar = {
  clipId: string;
  tipo: "borrado" | "voz" | "1080p" | "calidad";
  jobId: string;
  sabor?: string;
};

export const MENSAJE_PROCESO_CORTADO =
  "Se cortó al cerrar el editor. Volvé a intentarlo.";
export const MENSAJE_ESCENA_CORTADA =
  "La generación se cortó al cerrar el editor. Tocá Reintentar: si ya había terminado, la traemos sin volver a cobrar.";
export const MENSAJE_SUBIDA_CORTADA =
  "El archivo no se terminó de subir antes de cerrar el editor. Volvé a subirlo.";

/**
 * Cómo se GUARDA un clip con procesos en curso. Lo que la pestaña está siguiendo no
 * se puede retomar desde la base (salvo la traducción, que guarda su job): se guarda
 * como error con el motivo, así quien vuelva a abrir el proyecto sabe qué pasó. La
 * pestaña viva no cambia: esto sólo arma lo que se persiste, y el próximo guardado
 * (cuando el proceso termine) lo pisa.
 */
export function clipParaGuardar<C extends ClipConProcesos>(clip: C): C {
  const copia: C = { ...clip };
  // Alias con el tipo base: con el genérico, TS no deja asignar los literales.
  const c: ClipConProcesos = copia;
  // Que un archivo no cargue es de ESA apertura: se recalcula cada vez que se abre
  // (firma o no firma, tiene o no tiene archivo). Guardado, una falla pasajera quedaba
  // como badge rojo para siempre aunque el video cargara bien.
  c.loadError = undefined;
  if (c.aiStatus === "generating") c.aiStatus = "draft";
  // Con su id de trabajo se dejan en curso y se retoman al abrir
  // (ADR-2026-11-03-los-procesos-se-retoman-al-volver); sin id (se cortaron antes de
  // que el backend los aceptara), se guardan con el motivo.
  if (c.eraseStatus === "processing" && !c.eraseJobId) {
    c.eraseStatus = "error";
    c.eraseError = MENSAJE_PROCESO_CORTADO;
  }
  if (c.corregirVozStatus === "processing" && !c.corregirVozJobId) {
    c.corregirVozStatus = "error";
    c.corregirVozError = MENSAJE_PROCESO_CORTADO;
  }
  if (c.pasarA1080pStatus === "processing" && !c.pasarA1080pExecId) {
    c.pasarA1080pStatus = "error";
    c.pasarA1080pError = MENSAJE_PROCESO_CORTADO;
  }
  if (c.mejorarCalidadStatus === "processing" && !c.mejorarCalidadJobId)
    c.mejorarCalidadStatus = undefined;
  if (c.voiceChangeStatus === "processing") {
    c.voiceChangeStatus = "error";
    c.voiceChangeError = MENSAJE_PROCESO_CORTADO;
  }
  // La traducción SIN job todavía (se cortó antes de que el backend la aceptara) no
  // hay cómo retomarla; con job se deja en curso y se retoma al abrir.
  if (c.aiTranslating && !c.aiTranslateJobId) {
    c.aiTranslating = false;
    c.aiTranslateError = MENSAJE_PROCESO_CORTADO;
  }
  return copia;
}

/**
 * Repara lo que se abrió de la base. Cubre también proyectos guardados ANTES de
 * `clipParaGuardar` (con "processing" pegado). Devuelve los clips y las traducciones
 * que hay que volver a seguir.
 */
export function repararAlAbrir<C extends ClipConProcesos>(
  clips: C[],
): {
  clips: C[];
  traduccionesARetomar: { clipId: string; jobId: string }[];
  procesosARetomar: ProcesoARetomar[];
} {
  const traduccionesARetomar: { clipId: string; jobId: string }[] = [];
  const procesosARetomar: ProcesoARetomar[] = [];
  const reparados = clips.map((original) => {
    // El `loadError` que trae el clip es el de ESTA apertura (lo pone la firma, que
    // corre antes): se conserva, aunque al guardar no se persista.
    const reparado: C = { ...clipParaGuardar(original), loadError: original.loadError };
    const c: ClipConProcesos = reparado;
    if (c.aiTranslating && c.aiTranslateJobId)
      traduccionesARetomar.push({ clipId: c.id, jobId: c.aiTranslateJobId });
    if (c.eraseStatus === "processing" && c.eraseJobId)
      procesosARetomar.push({ clipId: c.id, tipo: "borrado", jobId: c.eraseJobId, sabor: c.eraseSabor });
    if (c.corregirVozStatus === "processing" && c.corregirVozJobId)
      procesosARetomar.push({ clipId: c.id, tipo: "voz", jobId: c.corregirVozJobId });
    if (c.pasarA1080pStatus === "processing" && c.pasarA1080pExecId)
      procesosARetomar.push({ clipId: c.id, tipo: "1080p", jobId: c.pasarA1080pExecId });
    if (c.mejorarCalidadStatus === "processing" && c.mejorarCalidadJobId)
      procesosARetomar.push({ clipId: c.id, tipo: "calidad", jobId: c.mejorarCalidadJobId });
    // Una escena con IA que volvió como borrador PERO con su execution_id: se había
    // pedido y no se vio el final (el id se borra al terminar bien o mal). Reintentar
    // reusa ese id y el backend devuelve lo ya generado sin cobrar de nuevo.
    if (c.type === "ai-generated" && c.aiStatus === "draft" && c.aiExecutionId) {
      c.aiStatus = "error";
      c.aiError = MENSAJE_ESCENA_CORTADA;
    }
    // Video, audio o imagen sin ningún lugar de dónde sacar el archivo: la subida no
    // terminó (se guarda sin el `blob:`). Sin esto volvía negro y mudo sin aviso.
    const esMedio = c.type === "video" || c.type === "audio" || c.type === "image";
    if (esMedio && !c.storagePath && !c.assetId && !c.imageUrl && !c.loadError)
      c.loadError = MENSAJE_SUBIDA_CORTADA;
    return reparado;
  });
  return { clips: reparados, traduccionesARetomar, procesosARetomar };
}

/** Campos del ARCHIVO de un clip: si el paso guardado y el clip de ahora son el mismo
 *  archivo, los de ahora son mejores (URL firmada vigente, la subida que ya terminó). */
const CAMPOS_DEL_ARCHIVO = ["src", "srcPreview", "srcPreviewDe", "storagePath", "assetId", "loadError"] as const;

type Proceso = {
  enCurso: (c: ClipConProcesos) => boolean;
  /** El estado del trabajo (en curso, su id, su error). */
  estado: string[];
  /** Lo que el trabajo escribe al TERMINAR (además del archivo): sólo se copia si en
   *  el paso estaba en curso y ahora ya terminó. Con el mismo trabajo en curso en los
   *  dos lados, lo del paso (un recorte que se deshace) se respeta (review Greptile #242). */
  resultado: string[];
  /** Su `duration` ya va dividida por la velocidad (material ÷ speed): al deshacer, se
   *  recalcula para la velocidad del paso. La traducción guarda el largo del archivo y no. */
  duracionPorVelocidad?: boolean;
  /** Cómo queda un clip que vuelve "en curso" sin nadie que lo siga. */
  cortado: (c: ClipConProcesos) => void;
};

export const MENSAJE_PROCESO_DESHECHO =
  "Se deshizo mientras se procesaba. Volvé a intentarlo.";

const PROCESOS: Proceso[] = [
  {
    enCurso: (c) => c.aiStatus === "generating",
    estado: ["aiStatus", "aiError", "aiGenerationStartedAt", "aiExecutionId", "aiExecutionParamsKey"],
    resultado: ["aiBorrador", "aiAssetWarning", "aiResolution", "duration", "sourceDuration", "transcript"],
    cortado: (c) => {
      // Con su execution_id, Reintentar trae lo ya generado sin volver a cobrar.
      c.aiStatus = c.aiExecutionId ? "error" : "draft";
      if (c.aiExecutionId) c.aiError = MENSAJE_ESCENA_CORTADA;
    },
  },
  {
    enCurso: (c) => c.eraseStatus === "processing",
    estado: ["eraseStatus", "eraseJobId", "eraseError", "eraseSabor", "eraseDesde"],
    // El archivo nuevo es sólo el tramo: su recorte viaja con él (review Greptile #243).
    resultado: [
      "originalSrcBeforeErase",
      "originalStoragePathBeforeErase",
      "transcript",
      "mediaOffset",
      "sourceDuration",
      "duration",
    ],
    duracionPorVelocidad: true,
    cortado: (c) => {
      c.eraseStatus = "error";
      c.eraseError = MENSAJE_PROCESO_DESHECHO;
      c.eraseJobId = undefined;
    },
  },
  {
    enCurso: (c) => c.voiceChangeStatus === "processing",
    estado: ["voiceChangeStatus", "voiceChangeError"],
    resultado: ["transcript"],
    cortado: (c) => {
      c.voiceChangeStatus = "error";
      c.voiceChangeError = MENSAJE_PROCESO_DESHECHO;
    },
  },
  {
    enCurso: (c) => c.corregirVozStatus === "processing",
    estado: ["corregirVozStatus", "corregirVozJobId", "corregirVozError", "corregirVozDesde"],
    resultado: ["transcript"],
    cortado: (c) => {
      c.corregirVozStatus = "error";
      c.corregirVozError = MENSAJE_PROCESO_DESHECHO;
      c.corregirVozJobId = undefined;
    },
  },
  {
    enCurso: (c) => c.pasarA1080pStatus === "processing",
    estado: ["pasarA1080pStatus", "pasarA1080pError", "pasarA1080pExecId"],
    resultado: ["aiResolution", "aiBorrador", "transcript"],
    cortado: (c) => {
      c.pasarA1080pStatus = "error";
      c.pasarA1080pError = MENSAJE_PROCESO_DESHECHO;
    },
  },
  {
    enCurso: (c) => c.mejorarCalidadStatus === "processing",
    estado: ["mejorarCalidadStatus", "mejorarCalidadJobId", "mejorarCalidadDesde"],
    resultado: ["transcript", "mediaOffset", "sourceDuration", "duration"],
    duracionPorVelocidad: true,
    cortado: (c) => {
      c.mejorarCalidadStatus = undefined;
    },
  },
  {
    enCurso: (c) => !!c.aiTranslating,
    estado: ["aiTranslating", "aiTranslateJobId", "aiTranslateError"],
    resultado: ["mediaOffset", "duration", "sourceDuration", "transcript"],
    cortado: (c) => {
      c.aiTranslating = false;
      c.aiTranslateJobId = undefined;
      c.aiTranslateError = MENSAJE_PROCESO_DESHECHO;
    },
  },
];

const copiar = (destino: Record<string, unknown>, origen: Record<string, unknown>, campos: readonly string[]) => {
  for (const k of campos) destino[k] = origen[k];
};

const velocidadDe = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : 1);

/**
 * La `duration` que escribió el trabajo es de AHORA, pero la `speed` es la del paso (eso
 * es lo que se deshace). Lo que manda es el material (duración × velocidad): se recalcula
 * la duración para la velocidad del paso, o un cambio a 2× hecho mientras corría dejaba
 * el clip con la mitad del video al deshacerlo (review Greptile #243). Sólo para los
 * procesos con `duracionPorVelocidad`.
 */
function conservarElMaterial(r: Record<string, unknown>, ahora: Record<string, unknown>): void {
  const vPaso = velocidadDe(r.speed);
  const vAhora = velocidadDe(ahora.speed);
  if (vPaso === vAhora || typeof ahora.duration !== "number") return;
  r.duration = (ahora.duration * vAhora) / vPaso;
}

/**
 * Un paso del Deshacer (o del Rehacer) combinado con cómo están AHORA los procesos —
 * ADR-2026-11-03-deshacer-no-revive-procesos.
 *
 * Cada paso guarda el arreglo de clips entero, también los estados de los procesos que
 * terminan solos (una escena que pasa a "lista", un borrado de objeto que vuelve). Al
 * aplicarlo crudo, Ctrl+Z dejaba la escena en "generando" para siempre (nadie la seguía)
 * y sin su archivo. Reglas, por clip del paso:
 * 1. En el paso está en curso y ahora ya terminó → se toma lo de ahora (el resultado).
 * 2. Ahora está en curso y en el paso no → se mantiene lo de ahora: el proceso sigue y
 *    su resultado tiene que caer en el clip.
 * 3. En el paso está en curso y ahora el clip no existe → nadie lo sigue: se marca como
 *    cortado (con su execution_id, Reintentar no vuelve a cobrar).
 * 4. Si es el mismo archivo, los campos del archivo son los de ahora (la URL vigente,
 *    la subida que terminó). Si el archivo es otro (deshacer un borrado de objeto ya
 *    hecho), se respeta el del paso: eso es lo que se está deshaciendo.
 */
export function conLosProcesosDeAhora<C extends ClipConProcesos>(paso: C[], ahora: C[]): C[] {
  const porId = new Map(ahora.map((c) => [c.id, c]));
  return paso.map((delPaso) => {
    const actual = porId.get(delPaso.id);
    const r: C = { ...delPaso };
    const rr = r as unknown as Record<string, unknown>;
    const aa = actual as unknown as Record<string, unknown> | undefined;
    let cambio = false;
    for (const p of PROCESOS) {
      const enElPaso = p.enCurso(delPaso);
      if (!actual) {
        if (enElPaso) {
          p.cortado(r);
          cambio = true;
        }
        continue;
      }
      const enCursoAhora = p.enCurso(actual);
      if (enElPaso && !enCursoAhora) {
        // Terminó: su estado, su resultado y el archivo son los de ahora.
        copiar(rr, aa!, p.estado);
        copiar(rr, aa!, p.resultado);
        if (p.duracionPorVelocidad) conservarElMaterial(rr, aa!);
        copiar(rr, aa!, CAMPOS_DEL_ARCHIVO);
        cambio = true;
      } else if (enCursoAhora) {
        // Sigue en curso (o arrancó después del paso): sólo su estado. Lo demás del
        // paso —un recorte, un movimiento— es lo que se está deshaciendo.
        copiar(rr, aa!, p.estado);
      }
    }
    if (actual && !cambio) {
      const mismoArchivo =
        (!!delPaso.storagePath && delPaso.storagePath === actual.storagePath) ||
        (!!delPaso.src && delPaso.src === actual.src);
      if (mismoArchivo) copiar(rr, aa!, CAMPOS_DEL_ARCHIVO);
    }
    return r;
  });
}
